'use client'

import type { HomeAIMessages } from '@/src/utils/homeAISearch/locale'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { Box, CircularProgress, IconButton, InputAdornment, TextField, alpha } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

interface AnimatedAISearchInputProps {
    showPlaceHolder: boolean
    messages: HomeAIMessages
    inputValue: string
    loading: boolean
    onInputChange: (nextValue: string) => void
    onSubmit: () => void
}

export const AnimatedAISearchInput = ({
    showPlaceHolder,
    messages,
    inputValue,
    loading,
    onInputChange,
    onSubmit
}: AnimatedAISearchInputProps) => {
    const [placeholderIndex, setPlaceholderIndex] = useState(0)
    const [charIndex, setCharIndex] = useState(0)
    const [isDeleting, setIsDeleting] = useState(false)

    const placeholderPool = useMemo(
        () => (messages.animatedPlaceholders.length ? messages.animatedPlaceholders : [messages.placeholder]),
        [messages.animatedPlaceholders, messages.placeholder]
    )
    const currentPlaceholder = placeholderPool[placeholderIndex] ?? messages.placeholder
    const animatedPlaceholder = currentPlaceholder.slice(0, charIndex)

    useEffect(() => {
        setPlaceholderIndex(0)
        setCharIndex(0)
        setIsDeleting(false)
    }, [messages])

    useEffect(() => {
        if (inputValue.trim()) return

        const typingSpeed = isDeleting ? 32 : 52
        const holdAtEnd = 1100
        const holdBeforeNext = 260

        const timeout = setTimeout(() => {
            if (!isDeleting && charIndex < currentPlaceholder.length) {
                setCharIndex(prev => prev + 1)
                return
            }

            if (!isDeleting && charIndex === currentPlaceholder.length) {
                setIsDeleting(true)
                return
            }

            if (isDeleting && charIndex > 0) {
                setCharIndex(prev => prev - 1)
                return
            }

            setIsDeleting(false)
            setPlaceholderIndex(prev => (prev + 1) % placeholderPool.length)
        }, !isDeleting && charIndex === currentPlaceholder.length
            ? holdAtEnd
            : isDeleting && charIndex === 0
                ? holdBeforeNext
                : typingSpeed)

        return () => clearTimeout(timeout)
    }, [charIndex, currentPlaceholder, inputValue, isDeleting, placeholderPool.length])

    return (
        <Box mt={3}>
            <TextField
                fullWidth
                minRows={1}
                maxRows={2}
                value={inputValue}
                onChange={e => onInputChange(e.target.value)}
                onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        onSubmit()
                    }
                }}
                placeholder={showPlaceHolder ? animatedPlaceholder : ' '}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start" >
                                <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end" >
                                <IconButton
                                    size="small"
                                    aria-label={messages.searchAriaLabel}
                                    onClick={onSubmit}
                                    disabled={loading || !inputValue.trim()}
                                    sx={theme => ({
                                        width: 42,
                                        height: 42,
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '&:hover': {
                                            bgcolor: theme.palette.primary.dark
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.35),
                                            color: alpha(theme.palette.primary.contrastText, 0.8)
                                        }
                                    })}
                                >
                                    {loading ? (
                                        <CircularProgress size={18} color="inherit" />
                                    ) : (
                                        <SearchIcon sx={{ fontSize: 20 }} />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        )
                    }
                }}
                sx={theme => ({
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 999,
                        pr: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.03)
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.25)
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.4)
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 1.5,
                        borderColor: 'primary.main'
                    },
                    '& .MuiInputBase-inputMultiline': {
                        maxHeight: 112,
                        overflowY: 'auto',
                        pr: 0.5
                    }
                })}
            />
        </Box>
    )
}
